import React, { useEffect, useState } from "react";
import ILeadData, {
  IAddress,
  ICategory,
  IColumn,
  ILeadSource,
  ISourceType,
} from "../../models/LeadData.model";
import Button, { ButtonType } from "../Assets/Button";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getStatesData } from "../../store/slices/kanbanSlice";

export interface LeadFormProps {
  initialData?: Partial<ILeadData>;
  onSubmit: (data: ILeadData) => Promise<void>;
  onClose: () => void;
  statusOptions: string[];
  inline?: boolean;
}

const LeadForm: React.FC<LeadFormProps> = ({
  initialData = {},
  onSubmit,
  onClose,
  inline = false,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  const kanbanColumns = useSelector((state: RootState) => state.kanban.columns);
  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const statusArray = React.useMemo(() => {
    return Object.values(kanbanColumns).map((column: IColumn) => column.name);
  }, [kanbanColumns]);
  const [Kanbanstatus, setKanbanStatus] = useState(statusArray?.[0]);

  const [name, setName] = useState(initialData.name || "");
  const [source, setSource] = useState<ILeadSource | undefined>(
    initialData.source
  );
  const [type, setType] = useState<ISourceType>(
    initialData.type || ISourceType.Hot
  );
  const [mobileNumber, setMobileNumber] = useState(
    initialData.mobileNumber || ""
  );
  const [sameAsMobile, setSameAsMobile] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState(
    initialData.whatsappNumber || ""
  );
  const [email, setEmail] = useState(initialData.email || "");
  const [address, setAddress] = useState<IAddress>({
    line1: "",
    state: "",
    city: "",
    district: "",
    pincode: "",
  });
  const [contactPersonName, setContactPersonName] = useState(
    initialData.contactPersonName || ""
  );
  const [showToTelecaller, setShowToTelecaller] = useState(
    initialData.showToTelecaller ?? false
  );

  const [category, setCategory] = useState<ICategory | undefined>(
    initialData.category
  );
  const [requirements, setRequirements] = useState(
    initialData.requirements || ""
  );

  // const statesData = india.states as {
  //   state: string;
  //   city: string[];
  //   districts: string[];
  // }[];

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    if (statusArray.length > 0) {
      setKanbanStatus(statusArray[0]);
    }
  }, [statusArray]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      return toast.error("Name is required");
    }
    if (!source) {
      return toast.error("Source is required");
    }
    if (!type) {
      return toast.error("Type is required");
    }
    if (!mobileNumber.trim()) {
      return toast.error("Mobile number is required");
    }

    if (!/^\d{10}$/.test(mobileNumber)) {
      return toast.error("Mobile number must be 10 digits.");
    }

    if (!contactPersonName.trim()) {
      return toast.error("Contact person name is required");
    }
    if (email && email.includes(" ")) {
      return toast.error("Email cannot contain spaces");
    }

    if (
      email &&
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
    ) {
      toast.error("Please provide a valid email address.");
      return;
    }

    if (email && email.includes(" ")) {
      return toast.error("Email cannot contain spaces");
    }

    if (!category) {
      return toast.error("Category is required");
    }

    if (
      !name ||
      !source ||
      !type ||
      !mobileNumber ||
      !contactPersonName ||
      !category
    ) {
      return toast.error("Please fill all the fields");
    }

    const payload: ILeadData = {
      card: {},
      status: Kanbanstatus || "lead",
      name,
      source: source!,
      type,
      mobileNumber,
      ...(sameAsMobile
        ? { whatsappNumber: mobileNumber }
        : { whatsappNumber: whatsappNumber || undefined }),
      ...(email.trim() && { email: email.trim() }),
      address,
      contactPersonName: contactPersonName.trim(),
      showToTelecaller,
      category: category!,
      requirements,
      showToBDE: false,
    };

    try {
      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Error submitting lead");
    }
  };

  return (
    <div
      className={
        inline
          ? ""
          : "fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[99] px-6"
      }
    >
      <div
        className={`bg-white ${inline ? "" : "backdrop-blur-md border border-white/20 shadow-2xl"
          } rounded-2xl p-8 w-full max-w-4xl ${inline ? "max-h-full" : "max-h-[90vh] overflow-y-auto"
          } relative`}
      >
        {!inline && (
          <div className="flex justify-between items-center border-b border-gray-300/40 pb-4 mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-700 tracking-wide">
              Add New Lead
            </h2>
            <button
              onClick={onClose}
              className="text-gray-300 hover:text-gray-600 text-2xl"
            >
              ✖
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Two-column grid for DataEntryBoard */}
          <div
            className={`grid ${inline ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
              } gap-6`}
          >
            {/* Left Column */}
            <div className="space-y-4">
              {/* Category */}
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ICategory)}
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Category</option>
                  {Object.values(ICategory).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter name"
                />
              </div>
              {/* Source */}

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Source <span className="text-red-500">*</span>
                </label>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value as ILeadSource)}
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Source</option>
                  {Object.values(ILeadSource).map((leadSource) => (
                    <option key={leadSource} value={leadSource}>
                      {leadSource}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as ISourceType)}
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  {Object.values(ISourceType).map((sourceType) => (
                    <option key={sourceType} value={sourceType}>
                      {sourceType}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mobile Number */}
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  type="tel"
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter mobile number"
                />
              </div>

              {/* WhatsApp */}
              <div className=" items-center ">
                {!sameAsMobile && (
                  <div className="flex-1">
                    <label className="block mb-1 text-xs font-medium text-gray-700">
                      WhatsApp Number
                    </label>
                    <input
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      type="tel"
                      className="w-full p-2 mb-4 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="WhatsApp number"
                    />
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    id="sameAsMobile"
                    type="checkbox"
                    checked={sameAsMobile}
                    onChange={(e) => setSameAsMobile(e.target.checked)}
                    className="h-4 w-4  text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="sameAsMobile"
                    className="ml-2 block text-xs text-gray-700"
                  >
                    Same as Mobile
                  </label>
                </div>
              </div>
            </div>

            {/* Contact Person */}
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  value={contactPersonName}
                  onChange={(e) => setContactPersonName(e.target.value)}
                  type="text"
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter contact person name"
                />
              </div>

              <div className="flex items-center">
                <input
                  id="showToTelecaller"
                  type="checkbox"
                  checked={showToTelecaller}
                  onChange={(e) => setShowToTelecaller(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="showToTelecaller"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Show to Telecaller
                </label>
              </div>

              {/* Right Column */}

              {/* Email */}
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Email
                </label>
                <input
                  value={email!}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter email"
                />
              </div>

              {/* Address Section */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-xs font-medium text-gray-700 mb-3">
                  Address Details <span className="text-red-500">*</span>
                </h3>
                <div className="space-y-3">
                  <input
                    value={address.line1}
                    onChange={(e) =>
                      setAddress((a) => ({ ...a, line1: e.target.value }))
                    }
                    placeholder="Address Line 1"
                    className="w-full p-2 text-xs border border-gray-300 rounded-md"
                  />

                  <select
                    value={(address.state || "").toLowerCase()}
                    onChange={(e) =>
                      setAddress({
                        line1: address.line1,
                        state: e.target.value,
                        city: "",
                        district: "",
                        pincode: address.pincode,
                      })
                    }
                    className="w-full p-2 text-xs border border-gray-300 rounded-md"
                  >
                    <option value="">Select State</option>
                    {statesData?.map((s) => (
                      <option key={s.state} value={(s.state || "").toLowerCase()}>
                        {s.state ? s.state.capitalizeWords() : ""}
                      </option>
                    ))}
                  </select>

                  <div className="grid grid-cols-9 gap-3">
                    <select
                      value={(address.city || "").toLowerCase()}
                      onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value, district: "" }))}
                      disabled={!address.state}
                      className="w-full p-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="">Select City</option>
                      {statesData
                        ?.find((s) => (s.state || "").toLowerCase() === (address.state || "").toLowerCase())
                        ?.city?.map((city: string) => (
                          <option key={city} value={city}>
                            {city ? city.capitalizeWords() : ""}
                          </option>
                        ))}
                    </select>

                    <select
                      value={(address.district || "").toLowerCase()}
                      onChange={(e) =>
                        setAddress((a) => ({ ...a, district: e.target.value }))
                      }
                      disabled={!address.state}
                      className="w-full p-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="">Select District</option>
                      {statesData
                        .find((s) => s.state === (address.state || "").toLowerCase())
                        ?.districts?.map((district: string) => (
                          <option key={district} value={district}>
                            {district.capitalizeWords()}
                          </option>
                        ))}
                    </select>
                  </div>
                  <input
                    value={address.pincode}
                    onChange={(e) =>
                      setAddress((a) => ({ ...a, pincode: e.target.value }))
                    }
                    placeholder="Pincode"
                    className="w-full p-2 text-xs border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-gray-700">
              Requirements
            </label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              className="w-full p-2 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter requirements"
              rows={3}
            ></textarea>
          </div>

          <div className="flex justify-end gap-2">
            <Button type={ButtonType.Submit} onClick={handleSubmit}>
              Submit
            </Button>
            <Button type={ButtonType.Cancel} onClick={onClose}>
              Close
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;
